import React from 'react';
import { cn } from '@/lib/utils';

interface AppMockupProps {
  showStatsView?: boolean;
  showCalendarView?: boolean;
  showFutureView?: boolean;
  className?: string;
}

/**
 * App Mockup Component
 * 
 * Renders mobile app mockups for Meduler medication management screens
 * Creates realistic UI representations of the statistics, calendar views and future vision
 * Uses SVG and HTML elements for optimal performance and styling
 * 
 * @param {boolean} showStatsView - Whether to show statistics view (default: false)
 * @param {boolean} showCalendarView - Whether to show calendar view (default: false)
 * @param {boolean} showFutureView - Whether to show future vision view (default: false)
 * @param {string} className - Additional classes for styling
 */
const AppMockup: React.FC<AppMockupProps> = ({
  showStatsView = false,
  showCalendarView = false,
  showFutureView = false,
  className,
}) => {
  return (
    <div className={cn('mockup-container', className)}>
      <div className="mockup-phone">
        {/* Phone Frame */}
        <div className="phone-frame bg-purple-600 rounded-[40px] p-2 shadow-xl">
          {/* Phone Notch */}
          <div className="notch flex justify-between items-center mb-1 px-4">
            <div className="text-white font-semibold">Meduler</div>
            <div className="flex space-x-2">
              <div className="text-white">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-bell">
                  <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"></path>
                  <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"></path>
                </svg>
              </div>
              <div className="text-white">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-clock">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
              </div>
            </div>
          </div>
          
          {/* Phone Content */}
          <div className={cn("phone-content rounded-[32px] h-[500px] overflow-hidden", {
            "bg-white": !showFutureView,
            "bg-purple-600": showFutureView
          })}>
            {showFutureView && (
              <div className="future-view p-5 text-white">
                {/* Daily Flow Section */}
                <div className="daily-flow-section bg-purple-700/30 rounded-2xl p-4 mb-4">
                  <div className="flex items-center gap-2 mb-3">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                    <h2 className="text-lg font-semibold">Your Daily Flow</h2>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <div className="w-24 text-sm">Morning</div>
                      <div className="flex-1 h-2.5 bg-purple-800/50 rounded-full overflow-hidden">
                        <div className="h-full bg-amber-300 rounded-full" style={{width: '75%'}}></div>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-24 text-sm">Noon</div>
                      <div className="flex-1 h-2.5 bg-purple-800/50 rounded-full overflow-hidden">
                        <div className="h-full bg-rose-300 rounded-full" style={{width: '45%'}}></div>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-24 text-sm">Evening</div>
                      <div className="flex-1 h-2.5 bg-purple-800/50 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-300 rounded-full" style={{width: '60%'}}></div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Next Integration Moment */}
                <div className="integration-moment bg-purple-700/30 rounded-2xl p-4 mb-4">
                  <div className="text-sm text-purple-200 mb-1">Next integration moment</div>
                  <h3 className="text-xl font-bold mb-2">Morning Routine +<br />Lisinopril</h3>
                  <div className="flex items-center gap-1 text-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                    Today, 8:00 AM with breakfast
                  </div>
                </div>
                
                {/* Integration Harmony */}
                <div className="harmony-section bg-white/90 text-purple-900 rounded-2xl p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                    <h2 className="text-lg font-semibold">Integration Harmony</h2>
                  </div>
                  
                  <div className="flex justify-around">
                    <div className="text-center">
                      <div className="text-sm text-gray-600">Flow Match</div>
                      <div className="text-3xl font-bold text-purple-600">92%</div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm text-gray-600">Consistency</div>
                      <div className="text-3xl font-bold text-purple-600">87%</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {showStatsView && (
              <div className="stats-view p-4">
                <div className="flex items-center gap-2 mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-purple-600">
                    <rect width="18" height="18" x="3" y="4" rx="2" ry="2"></rect>
                    <line x1="16" x2="16" y1="2" y2="6"></line>
                    <line x1="8" x2="8" y1="2" y2="6"></line>
                    <line x1="3" x2="21" y1="10" y2="10"></line>
                  </svg>
                  <h2 className="text-lg font-semibold">Medication Stats</h2>
                </div>
                
                <div className="text-sm text-gray-600 mb-6">Monthly Overview</div>
                
                <div className="grid grid-cols-2 gap-6 mb-6">
                  <div className="stats-card">
                    <div className="text-sm text-gray-500">Total Medications</div>
                    <div className="text-3xl font-bold mt-1">3</div>
                  </div>
                  <div className="stats-card">
                    <div className="text-sm text-gray-500">Active Medications</div>
                    <div className="text-3xl font-bold mt-1">3</div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-6 mb-8">
                  <div className="stats-card">
                    <div className="text-sm text-gray-500">Dosage Adherence</div>
                    <div className="text-3xl font-bold text-purple-600 mt-1">92%</div>
                  </div>
                  <div className="stats-card">
                    <div className="text-sm text-gray-500">Refills Needed</div>
                    <div className="text-3xl font-bold text-amber-500 mt-1">1</div>
                  </div>
                </div>
                
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <div className="text-sm font-medium">Medication Types</div>
                    <div className="text-xs text-purple-600">March 2025</div>
                  </div>
                  
                  <div className="medication-types space-y-2">
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full bg-purple-500 mr-2"></div>
                      <div className="text-sm">Cycle-Based</div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                      <div className="text-sm">Daily</div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full bg-amber-500 mr-2"></div>
                      <div className="text-sm">Fixed Duration</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {showCalendarView && (
              <div className="calendar-view">
                <div className="p-4">
                  <div className="flex justify-between items-center mb-4">
                    <div className="text-lg font-semibold">Medication Calendar</div>
                    <div className="text-sm bg-gray-100 rounded-md px-2 py-1">
                      Show Stats
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center mb-4">
                    <div className="text-sm text-gray-800 font-medium">
                      March 2025
                    </div>
                    <div className="flex items-center space-x-4">
                      <button className="text-gray-600">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="m15 18-6-6 6-6"></path>
                        </svg>
                      </button>
                      <div className="text-sm font-medium">Today</div>
                      <button className="text-gray-600">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="m9 18 6-6-6-6"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center space-x-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                      </svg>
                      <span className="text-xs">Filter</span>
                    </div>
                    <div className="text-xs text-gray-600">6 meds</div>
                    <div className="flex space-x-2">
                      <button className="text-gray-600">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect>
                          <line x1="3" x2="21" y1="9" y2="9"></line>
                          <line x1="3" x2="21" y1="15" y2="15"></line>
                          <line x1="9" x2="9" y1="3" y2="21"></line>
                          <line x1="15" x2="15" y1="3" y2="21"></line>
                        </svg>
                      </button>
                      <button className="text-gray-600">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="12" x2="12" y1="8" y2="12"></line>
                          <line x1="12" x2="12.01" y1="16" y2="16"></line>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Calendar Grid */}
                <div className="calendar-grid">
                  <div className="grid grid-cols-7 text-center text-xs font-medium py-2">
                    <div>Sun</div>
                    <div>Mon</div>
                    <div>Tue</div>
                    <div>Wed</div>
                    <div>Thu</div>
                    <div>Fri</div>
                    <div>Sat</div>
                  </div>
                  
                  <div className="grid grid-cols-7 text-center text-xs border-t border-gray-200">
                    <div className="p-2 border-r border-b border-gray-200 text-gray-400">23</div>
                    <div className="p-2 border-r border-b border-gray-200 text-gray-400">24</div>
                    <div className="p-2 border-r border-b border-gray-200 text-gray-400">25</div>
                    <div className="p-2 border-r border-b border-gray-200 text-gray-400">26</div>
                    <div className="p-2 border-r border-b border-gray-200 text-gray-400">27</div>
                    <div className="p-2 border-r border-b border-gray-200 text-gray-400">28</div>
                    <div className="p-2 border-b border-gray-200">1</div>
                  </div>
                  
                  <div className="grid grid-cols-7 text-center text-xs border-b border-gray-200">
                    <div className="p-2 border-r border-gray-200">2</div>
                    <div className="p-2 border-r border-gray-200">3</div>
                    <div className="p-2 border-r border-gray-200 relative">
                      4
                      <div className="absolute bottom-0 left-0 right-0 flex justify-center">
                        <div className="bg-green-500 h-1 w-1 rounded-full"></div>
                      </div>
                    </div>
                    <div className="p-2 border-r border-gray-200 relative">
                      5
                      <div className="absolute bottom-0 left-0 right-0 flex justify-center">
                        <div className="bg-green-500 h-1 w-1 rounded-full"></div>
                      </div>
                    </div>
                    <div className="p-2 border-r border-gray-200 relative">
                      6
                      <div className="absolute bottom-0 left-0 right-0 flex justify-center">
                        <div className="bg-green-500 h-1 w-1 rounded-full"></div>
                      </div>
                    </div>
                    <div className="p-2 border-r border-gray-200 relative">
                      7
                      <div className="absolute bottom-0 left-0 right-0 flex justify-center">
                        <div className="bg-green-500 h-1 w-1 rounded-full"></div>
                      </div>
                    </div>
                    <div className="p-2 relative">
                      8
                      <div className="absolute bottom-0 left-0 right-0 flex justify-center">
                        <div className="bg-green-500 h-1 w-1 rounded-full"></div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-7 text-center text-xs border-b border-gray-200 bg-blue-50">
                    <div className="p-2 border-r border-gray-200">9</div>
                    <div className="p-2 border-r border-gray-200">10</div>
                    <div className="p-2 border-r border-gray-200 relative">
                      11
                      <div className="absolute bottom-0 left-0 right-0 flex justify-center">
                        <div className="bg-green-500 h-1 w-1 rounded-full"></div>
                      </div>
                    </div>
                    <div className="p-2 border-r border-gray-200 bg-blue-100 font-medium relative">
                      12
                      <div className="absolute bottom-0 left-0 right-0 flex justify-center">
                        <div className="bg-green-500 h-1 w-1 rounded-full"></div>
                      </div>
                    </div>
                    <div className="p-2 border-r border-gray-200 relative">
                      13
                      <div className="absolute bottom-0 left-0 right-0 flex justify-center">
                        <div className="bg-green-500 h-1 w-1 rounded-full"></div>
                      </div>
                    </div>
                    <div className="p-2 border-r border-gray-200 relative">
                      14
                      <div className="absolute bottom-0 left-0 right-0 flex justify-center">
                        <div className="bg-green-500 h-1 w-1 rounded-full"></div>
                      </div>
                    </div>
                    <div className="p-2 relative">
                      15
                      <div className="absolute bottom-0 left-0 right-0 flex justify-center">
                        <div className="bg-green-500 h-1 w-1 rounded-full"></div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-7 text-center text-xs">
                    <div className="p-2 border-r border-gray-200">16</div>
                    <div className="p-2 border-r border-gray-200">17</div>
                    <div className="p-2 border-r border-gray-200">18</div>
                    <div className="p-2 border-r border-gray-200">19</div>
                    <div className="p-2 border-r border-gray-200">20</div>
                    <div className="p-2 border-r border-gray-200">21</div>
                    <div className="p-2">22</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppMockup;
